import {useCallback} from 'react';
import {useMutation, useQueries, useQuery, useQueryClient} from '@tanstack/react-query';
import {useAuth} from '../auth/useAuth';
import * as api from './driveApi';
import {DriveApiError} from './driveApi';
import {BIN_NAMES, DriveComment, DriveDriveInfo, DriveFile, DriveRevision, isFolder, isImageFile} from './types';
import {queryKeys} from './queryKeys';
import {readJsonFile, writeJsonFile} from './jsonFileStore';
import {findFileInListings, patchFileInListings, seedJsonFile} from './cacheUpdates';
import {settleAll} from './settleAll';
import {getPictureCoverFileName, parseEventInfo, EventInfo} from '../domain/eventInfo';
import {JsonFileContent, readJsonContent, writtenJsonContent} from '../domain/json';
import {parseDriveYear} from '../domain/naming';
import {AlbumPlan} from '../domain/albumTree';

/**
 * Wraps a Drive call with the current access token, transparently retrying once with a
 * forced silent refresh if the token turns out to have been rejected (401).
 */
export function useDriveCall() {
    const {getValidAccessToken} = useAuth();
    return useCallback(async <T,>(fn: (token: string) => Promise<T>): Promise<T> => {
        const token = await getValidAccessToken();
        try {
            return await fn(token);
        } catch (e) {
            if (e instanceof DriveApiError && e.status === 401) {
                const refreshed = await getValidAccessToken(true);
                return await fn(refreshed);
            }
            throw e;
        }
    }, [getValidAccessToken]);
}

export function useDrives() {
    const callDrive = useDriveCall();
    return useQuery<DriveDriveInfo[]>({
        queryKey: queryKeys.drives(),
        queryFn: () => callDrive(api.listSharedDrives),
    });
}

/** Resolves the shared drive whose "SPOT - <year>" name matches the given year. */
export function useYearDrive(year: number | undefined) {
    const drivesQuery = useDrives();
    const drive = year !== undefined
        ? drivesQuery.data?.find((d) => parseDriveYear(d.name) === year)
        : undefined;
    return {...drivesQuery, drive};
}

function folderChildrenQuery(
    callDrive: ReturnType<typeof useDriveCall>,
    driveId: string | undefined,
    folderId: string | undefined,
) {
    return {
        queryKey: queryKeys.files(folderId),
        queryFn: () => callDrive((token) => api.listFilesInFolder(token, driveId as string, folderId as string)),
        enabled: !!driveId && !!folderId,
    };
}

/** Lists the direct children of a folder (or a shared drive's root, since driveId === root folder id). */
export function useFolderChildren(driveId: string | undefined, folderId: string | undefined) {
    const callDrive = useDriveCall();
    return useQuery<DriveFile[]>(folderChildrenQuery(callDrive, driveId, folderId));
}

/** Like `useFolderChildren` for several folders at once (same cache entries), e.g. every bin of an album. */
export function useFoldersChildren(driveId: string | undefined, folderIds: (string | undefined)[]) {
    const callDrive = useDriveCall();
    return useQueries({
        queries: folderIds.map((folderId) => folderChildrenQuery(callDrive, driveId, folderId)),
    });
}

/**
 * A JSON file's content by id (minified, see `JsonFileContent`), parsed with `parse` — pass a
 * stable, module-level function (it runs as `select`). Given the file's Drive `version` (from a
 * listing), the content of that version is treated as immutable: fetched once per change and
 * persisted across reloads. Without a version it goes stale like any other query.
 */
export function useJsonFile<T>(
    fileId: string | undefined,
    parse: (content: JsonFileContent) => T,
    options: { version?: string; enabled?: boolean } = {},
) {
    const callDrive = useDriveCall();
    const {version, enabled = true} = options;
    return useQuery<JsonFileContent, unknown, T>({
        queryKey: queryKeys.jsonFile(fileId, version),
        queryFn: async () => {
            const content = readJsonContent(await callDrive((token) => api.getFileMediaText(token, fileId as string)));
            if (version) writeJsonFile(fileId as string, version, content);
            return content;
        },
        select: parse,
        enabled: !!fileId && enabled,
        staleTime: version ? Infinity : undefined,
        initialData: () => (fileId && version ? readJsonFile(fileId, version)?.content : undefined),
        initialDataUpdatedAt: () => (fileId && version ? readJsonFile(fileId, version)?.savedAt : undefined),
    });
}

const parseEventInfoContent = (content: JsonFileContent) => parseEventInfo(content.text);

/** A folder's info.json (as listed), parsed; `null` data means the file isn't valid event info. */
export function useEventInfo(infoFile: DriveFile | undefined, options: { enabled?: boolean } = {}) {
    return useEventInfoWithParser(infoFile, parseEventInfoContent, options);
}

export function useEventInfoWithParser<T>(infoFile: DriveFile | undefined, 
        parser: (content: JsonFileContent) => T | null, options: { enabled?: boolean } = {}) {
    return useJsonFile(infoFile?.id, parser, {version: infoFile?.version, enabled: options.enabled});
}

function indexByParent(files: DriveFile[]): Map<string, DriveFile> {
    const index = new Map<string, DriveFile>();
    for (const file of files) {
        const parentId = file.parents?.[0];
        if (parentId) index.set(parentId, file);
    }
    return index;
}

/** Every info.json in a year's shared drive, by parent folder id — one search serves all event folder cards. */
export function useInfoFileIndex(driveId: string | undefined, enabled: boolean) {
    const callDrive = useDriveCall();
    return useQuery<DriveFile[], unknown, Map<string, DriveFile>>({
        queryKey: queryKeys.infoIndex(driveId),
        queryFn: () => callDrive((token) => api.listFilesByName(token, driveId as string, 'info.json')),
        select: indexByParent,
        enabled: !!driveId && enabled,
    });
}

/**
 * An album's cover picture: the file its info.json `cover` names in the album's web bin, or null when
 * there is no such picture. The album's listing is the cache entry browsing it uses; the cover is then
 * looked up by name rather than by listing the whole bin (unless that listing is cached already).
 */
export function useAlbumCover(driveId: string | undefined, albumFolderId: string, cover: string | undefined, enabled = true) {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    const coverName = getPictureCoverFileName(cover) ?? undefined;
    const albumListing = useFolderChildren(driveId, enabled && coverName ? albumFolderId : undefined);
    const webFolderId = albumListing.data?.find((f) => isFolder(f) && f.name === 'web')?.id;
    const findIn = (files: DriveFile[]) => files.find((f) => f.name === coverName && isImageFile(f)) ?? null;
    return useQuery<DriveFile | null>({
        queryKey: queryKeys.albumCover(webFolderId, coverName),
        queryFn: async () => findIn(await callDrive((token) =>
            api.listFilesInFolderByName(token, driveId as string, webFolderId as string, coverName as string))),
        initialData: () => {
            const webListing = webFolderId && queryClient.getQueryData<DriveFile[]>(queryKeys.files(webFolderId));
            return webListing ? findIn(webListing) : undefined;
        },
        initialDataUpdatedAt: () => webFolderId ? queryClient.getQueryState(queryKeys.files(webFolderId))?.dataUpdatedAt : undefined,
        enabled: !!driveId && !!webFolderId && !!coverName,
    });
}

export interface MoveFilesVars {
    fileIds: string[];
    addParentId: string;
    removeParentId: string;
}

/** Moves files between two folders; both listings are refetched once, after every move has settled. */
export function useMoveFiles() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: MoveFilesVars) => settleAll(vars.fileIds.map((fileId) =>
            callDrive((token) => api.moveFile(token, fileId, vars.addParentId, vars.removeParentId)))),
        // Also after a partial failure, since some of the files may already have moved.
        onSettled: (_data, _error, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.addParentId)});
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.removeParentId)});
        },
    });
}

export interface CopyFilesVars {
    /** Each file to copy, with the name its copy gets. */
    copies: { fileId: string; name: string }[];
    parentId: string;
}

/** Copies files into one folder; its listing is refetched once, after every copy has settled. */
export function useCopyFiles() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: CopyFilesVars) => settleAll(vars.copies.map(({fileId, name}) =>
            callDrive((token) => api.copyFile(token, fileId, name, vars.parentId)))),
        // Also after a partial failure, since some of the copies may already exist.
        onSettled: (_data, _error, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentId)});
        },
    });
}

export interface DeleteFilesVars {
    fileIds: string[];
    parentId: string;
}

/** Permanently deletes files from one folder; its listing is refetched once, after every delete has settled. */
export function useDeleteFiles() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: DeleteFilesVars) => settleAll(vars.fileIds.map((fileId) =>
            callDrive((token) => api.deleteFile(token, fileId)))),
        // Also after a partial failure, since some of the files may already be gone.
        onSettled: (_data, _error, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentId)});
        },
    });
}

export interface AddToPlazmaVars {
    file: DriveFile;
    /** The album folder holding the bins — the plazma bin is created here if it doesn't exist yet. */
    albumFolderId: string;
    plazmaFolderId: string | undefined;
}

/** Copies a (web) photo into the album's plazma bin, creating the bin on first use. */
export function useAddToPlazma() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (vars: AddToPlazmaVars) => {
            let plazmaFolderId = vars.plazmaFolderId;
            if (!plazmaFolderId) {
                const folder = await callDrive((token) => api.createFolder(token, 'plazma', vars.albumFolderId));
                // Surface the new bin right away so follow-up stars reuse it instead of creating another.
                queryClient.setQueryData<DriveFile[]>(queryKeys.files(vars.albumFolderId), (old) => old ? [...old, folder] : old);
                queryClient.invalidateQueries({queryKey: queryKeys.files(vars.albumFolderId)});
                // A brand-new folder is empty; seeding its listing avoids a loading state (hidden stars).
                queryClient.setQueryData<DriveFile[]>(queryKeys.files(folder.id), []);
                plazmaFolderId = folder.id;
            }
            const copy = await callDrive((token) =>
                api.copyFile(token, vars.file.id, vars.file.name, plazmaFolderId as string));
            return {copy, plazmaFolderId};
        },
        onSuccess: ({copy, plazmaFolderId}) => {
            // Fill the star immediately, then reconcile with the server.
            queryClient.setQueryData<DriveFile[]>(queryKeys.files(plazmaFolderId), (old) =>
                old?.some((f) => f.id === copy.id) ? old : [...(old ?? []), copy]);
            queryClient.invalidateQueries({queryKey: queryKeys.files(plazmaFolderId)});
        },
    });
}

export interface RemoveFromPlazmaVars {
    /** The plazma copies to remove (not the web originals). */
    fileIds: string[];
    plazmaFolderId: string;
}

/** Deletes plazma copies; the web originals are untouched. */
export function useRemoveFromPlazma() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: RemoveFromPlazmaVars) =>
            settleAll(vars.fileIds.map((id) => callDrive((token) => api.deleteFile(token, id)))),
        onSuccess: (_data, vars) => {
            queryClient.setQueryData<DriveFile[]>(queryKeys.files(vars.plazmaFolderId), (old) =>
                old?.filter((f) => !vars.fileIds.includes(f.id)));
        },
        // Also after a partial failure, since some of the copies may already be gone.
        onSettled: (_data, _error, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.plazmaFolderId)});
        },
    });
}

export interface CreateAlbumVars {
    driveId: string;
    /** The folder the album is created in: the drive itself (the year root) or any folder in it. */
    parentId: string;
    /** The album and its sub-albums, see `planAlbumTree`. */
    plan: AlbumPlan;
}

/**
 * Creates an album in one mutation: every folder of the plan (depth first, the root in `parentId`),
 * each with its own info.json, and the web/archive/plazma bins in the leaf albums.
 */
export function useCreateAlbum() {
    const beautify = true;  // always beautify new info.json files
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (vars: CreateAlbumVars) => {
            async function createAlbumFolder(plan: AlbumPlan, parentId: string): Promise<DriveFile> {
                const folder = await callDrive((token) => api.createFolder(token, plan.folderName, parentId));
                const infoFile = await callDrive((token) => api.createJsonFile(token, 'info.json', folder.id, plan.info, beautify));
                // The new album's card can show its title without downloading what we just wrote.
                seedJsonFile(queryClient, infoFile, writtenJsonContent(plan.info, beautify));
                if (plan.bins) {
                    await Promise.all(
                        BIN_NAMES.map((bin) => callDrive((token) => api.createFolder(token, bin, folder.id)))
                    );
                }
                for (const child of plan.children) {
                    await createAlbumFolder(child, folder.id);
                }
                return folder;
            }

            return createAlbumFolder(vars.plan, vars.parentId);
        },
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentId)});
            queryClient.invalidateQueries({queryKey: queryKeys.infoIndex(vars.driveId)});
        },
    });
}

export interface CreateFolderVars {
    name: string;
    parentId: string;
}

/** Generic "+ New folder" action — adds a plain custom subfolder anywhere. */
export function useCreateFolder() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: CreateFolderVars) => callDrive((token) => api.createFolder(token, vars.name, vars.parentId)),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentId)});
        },
    });
}

export interface RenameFileVars {
    fileId: string;
    newName: string;
    parentId: string;
}

export function useRenameFile() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: RenameFileVars) => callDrive((token) => api.renameFile(token, vars.fileId, vars.newName)),
        onSuccess: (renamed, vars) => {
            // Names are cached indefinitely for breadcrumbs (and seeded from listings), so put the new
            // name everywhere now rather than waiting for the listing refetch.
            patchFileInListings(queryClient, renamed);
            queryClient.setQueryData<DriveFile>(queryKeys.fileMeta(vars.fileId), (old) =>
                old ? {...old, name: renamed.name} : old);
            queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentId)});
        },
    });
}

function fileMetaQuery(
    callDrive: ReturnType<typeof useDriveCall>,
    queryClient: ReturnType<typeof useQueryClient>,
    fileId: string | undefined,
) {
    return {
        queryKey: queryKeys.fileMeta(fileId),
        queryFn: () => callDrive((token) => api.getFile(token, fileId as string, 'id,name,mimeType')),
        enabled: !!fileId,
        staleTime: Infinity,
        // A folder we navigated into is in its parent's cached listing — no need to ask Drive.
        initialData: () => (fileId ? findFileInListings(queryClient, fileId) : undefined),
    };
}

/** Thin cached lookup of a file/folder's own name — used to resolve breadcrumb labels. */
export function useFileMeta(fileId: string | undefined) {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useQuery<DriveFile>(fileMetaQuery(callDrive, queryClient, fileId));
}

/** Like `useFileMeta` for several files at once (same cache entries), e.g. every folder of a path. */
export function useFileMetas(fileIds: string[]) {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useQueries({
        queries: fileIds.map((fileId) => fileMetaQuery(callDrive, queryClient, fileId)),
    });
}

export interface SaveEventInfoVars {
    /** The info.json to overwrite; undefined to create one in `parentFolderId`. */
    fileId: string | undefined;
    parentFolderId: string;
    info: EventInfo;
    beautify?: boolean;
}

/** Updates an info.json in place, or creates one in the folder if it has none yet. */
export function useSaveEventInfo() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: SaveEventInfoVars) => vars.fileId
            ? callDrive((token) => api.updateJsonFileContent(token, vars.fileId as string, vars.info, vars.beautify))
            : callDrive((token) => api.createJsonFile(token, 'info.json', vars.parentFolderId, vars.info, vars.beautify)),
        onSuccess: (file, vars) => {
            // The response carries the new version: seeding the content and patching the listings
            // that show the file keeps everything current without refetching them.
            seedJsonFile(queryClient, file, writtenJsonContent(vars.info, vars.beautify));
            if (vars.fileId) {
                patchFileInListings(queryClient, file);
            } else {
                queryClient.invalidateQueries({queryKey: queryKeys.files(vars.parentFolderId)});
                queryClient.invalidateQueries({queryKey: queryKeys.allInfoIndexes()});
            }
        },
    });
}

/** All comment threads on a file (resolved ones included — filter with `getOpenThreads`). */
export function useFileComments(fileId: string | undefined) {
    const callDrive = useDriveCall();
    return useQuery<DriveComment[]>({
        queryKey: queryKeys.comments(fileId),
        queryFn: () => callDrive((token) => api.listComments(token, fileId as string)),
        enabled: !!fileId,
    });
}

/** The stored versions of a file's content, oldest first (see `numberRevisions`). */
export function useFileRevisions(fileId: string | undefined) {
    const callDrive = useDriveCall();
    return useQuery<DriveRevision[]>({
        queryKey: queryKeys.revisions(fileId),
        queryFn: () => callDrive((token) => api.listRevisions(token, fileId as string)),
        enabled: !!fileId,
    });
}

export interface AddCommentVars {
    fileId: string;
    content: string;
    /** E.g. a region of the picture (`formatRectAnchor`). */
    anchor?: string;
}

export function useAddComment() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: AddCommentVars) => callDrive((token) => api.createComment(token, vars.fileId, vars.content, vars.anchor)),
        onSuccess: (comment, vars) => {
            // Show the new thread immediately, then reconcile with the server.
            queryClient.setQueryData<DriveComment[]>(queryKeys.comments(vars.fileId), (old) => old ? [...old, comment] : old);
            queryClient.invalidateQueries({queryKey: queryKeys.comments(vars.fileId)});
        },
    });
}

export interface ReplyToCommentVars {
    fileId: string;
    commentId: string;
    content: string;
}

export function useReplyToComment() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: ReplyToCommentVars) =>
            callDrive((token) => api.createReply(token, vars.fileId, vars.commentId, {content: vars.content})),
        onSuccess: (reply, vars) => {
            queryClient.setQueryData<DriveComment[]>(queryKeys.comments(vars.fileId), (old) => old?.map((c) =>
                c.id === vars.commentId ? {...c, replies: [...(c.replies ?? []), reply]} : c
            ));
            queryClient.invalidateQueries({queryKey: queryKeys.comments(vars.fileId)});
        },
    });
}

export interface ResolveCommentVars {
    fileId: string;
    commentId: string;
}

/** Resolves a thread by posting an action-only reply, as the Drive comments API requires. */
export function useResolveComment() {
    const callDrive = useDriveCall();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars: ResolveCommentVars) =>
            callDrive((token) => api.createReply(token, vars.fileId, vars.commentId, {action: 'resolve'})),
        onSuccess: (_reply, vars) => {
            queryClient.setQueryData<DriveComment[]>(queryKeys.comments(vars.fileId), (old) => old?.map((c) =>
                c.id === vars.commentId ? {...c, resolved: true} : c
            ));
            queryClient.invalidateQueries({queryKey: queryKeys.comments(vars.fileId)});
        },
    });
}
